import { useCallback, useMemo } from "react";
import { Message, MessagesState } from "@/lib/core/crm/types";

export function useSetters(setState: React.Dispatch<React.SetStateAction<MessagesState>>) {
  // Set messages for an inbox/provider
  const setMessages = useCallback(
    (
      inboxId: string,
      provider: string,
      messages: Message[],
      hasMore: boolean,
      nextPageToken?: string
    ) => {
      setState((prev) => ({
        ...prev,
        messagesByInbox: {
          ...prev.messagesByInbox,
          [inboxId]: {
            ...prev.messagesByInbox[inboxId],
            [provider]: messages,
          },
        },
        paginationInfo: {
          ...prev.paginationInfo,
          [inboxId]: {
            ...prev.paginationInfo[inboxId],
            [provider]: { hasMore, nextPageToken },
          },
        },
        unreadCounts: {
          ...prev.unreadCounts,
          [inboxId]: {
            ...prev.unreadCounts[inboxId],
            [provider]: messages.filter((msg) => !msg.isRead).length,
          },
        },
      }));
    },
    []
  );

  // Add messages to existing list (for pagination)
  const addMessages = useCallback(
    (
      inboxId: string,
      provider: string,
      messages: Message[],
      hasMore: boolean,
      nextPageToken?: string
    ) => {
      setState((prev) => {
        const existingMessages = prev.messagesByInbox[inboxId]?.[provider] || [];
        const allMessages = [...existingMessages, ...messages];

        return {
          ...prev,
          messagesByInbox: {
            ...prev.messagesByInbox,
            [inboxId]: {
              ...prev.messagesByInbox[inboxId],
              [provider]: allMessages,
            },
          },
          paginationInfo: {
            ...prev.paginationInfo,
            [inboxId]: {
              ...prev.paginationInfo[inboxId],
              [provider]: { hasMore, nextPageToken },
            },
          },
          unreadCounts: {
            ...prev.unreadCounts,
            [inboxId]: {
              ...prev.unreadCounts[inboxId],
              [provider]: allMessages.filter((msg) => !msg.isRead).length,
            },
          },
        };
      });
    },
    []
  );

  // Update a specific message
  const updateMessage = useCallback(
    (inboxId: string, provider: string, messageId: string, updates: Partial<Message>) => {
      setState((prev) => {
        const messages = prev.messagesByInbox[inboxId]?.[provider] || [];
        const updatedMessages = messages.map((msg) =>
          msg.id === messageId ? { ...msg, ...updates } : msg
        );

        return {
          ...prev,
          messagesByInbox: {
            ...prev.messagesByInbox,
            [inboxId]: {
              ...prev.messagesByInbox[inboxId],
              [provider]: updatedMessages,
            },
          },
          unreadCounts: {
            ...prev.unreadCounts,
            [inboxId]: {
              ...prev.unreadCounts[inboxId],
              [provider]: updatedMessages.filter((msg) => !msg.isRead).length,
            },
          },
        };
      });
    },
    []
  );

  // Set loading state
  const setLoading = useCallback((inboxId: string, provider: string, loading: boolean) => {
    setState((prev) => ({
      ...prev,
      loadingStates: {
        ...prev.loadingStates,
        [inboxId]: {
          ...prev.loadingStates[inboxId],
          [provider]: loading,
        },
      },
    }));
  }, []);

  // Set error state
  const setError = useCallback((inboxId: string, provider: string, error: string | null) => {
    setState((prev) => ({
      ...prev,
      errorStates: {
        ...prev.errorStates,
        [inboxId]: {
          ...prev.errorStates[inboxId],
          [provider]: error,
        },
      },
    }));
  }, []);

  // Clear messages for an inbox/provider
  const clearMessages = useCallback((inboxId: string, provider: string) => {
    setState((prev) => {
      const newState = { ...prev };

      // Remove messages
      if (newState.messagesByInbox[inboxId]) {
        delete newState.messagesByInbox[inboxId][provider];
        if (Object.keys(newState.messagesByInbox[inboxId]).length === 0) {
          delete newState.messagesByInbox[inboxId];
        }
      }

      // Remove loading state
      if (newState.loadingStates[inboxId]) {
        delete newState.loadingStates[inboxId][provider];
        if (Object.keys(newState.loadingStates[inboxId]).length === 0) {
          delete newState.loadingStates[inboxId];
        }
      }

      // Remove error state
      if (newState.errorStates[inboxId]) {
        delete newState.errorStates[inboxId][provider];
        if (Object.keys(newState.errorStates[inboxId]).length === 0) {
          delete newState.errorStates[inboxId];
        }
      }

      // Remove pagination info
      if (newState.paginationInfo[inboxId]) {
        delete newState.paginationInfo[inboxId][provider];
        if (Object.keys(newState.paginationInfo[inboxId]).length === 0) {
          delete newState.paginationInfo[inboxId];
        }
      }

      // Remove unread count
      if (newState.unreadCounts[inboxId]) {
        delete newState.unreadCounts[inboxId][provider];
        if (Object.keys(newState.unreadCounts[inboxId]).length === 0) {
          delete newState.unreadCounts[inboxId];
        }
      }

      return newState;
    });
  }, []);

  // Every member has an empty dep array, so this slice is allocated once for
  // the lifetime of the provider. A fresh object literal here would defeat the
  // provider's useMemo — see docs/engineering-rules/MEMO_STABLE_PROPS.md.
  return useMemo(
    () => ({
      setMessages,
      addMessages,
      updateMessage,
      setLoading,
      setError,
      clearMessages,
    }),
    [setMessages, addMessages, updateMessage, setLoading, setError, clearMessages]
  );
}
